import React, { useContext, useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import IconButton from "@material-ui/core/IconButton";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import MessageVariablesPicker from "../MessageVariablesPicker";
import ButtonWithSpinner from "../ButtonWithSpinner";

import {
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Tooltip,
    Typography,
} from "@material-ui/core";
import ConfirmationModal from "../ConfirmationModal";
import {
    getQuickMessageMedias,
    MAX_QUICKMESSAGE_MEDIAS,
} from "../../utils/quickMessageMedias";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    multFieldLine: {
        display: "flex",
        "& > *:not(:last-child)": {
            marginRight: theme.spacing(1),
        },
    },

    btnWrapper: {
        position: "relative",
    },

    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    colorAdorment: {
        width: 20,
        height: 20,
    },
    attachmentRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
    },
    attachmentName: {
        textTransform: "none",
        textAlign: "left",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        flex: 1,
        minWidth: 0,
    },
}));

const QuickeMessageSchema = Yup.object().shape({
    shortcode: Yup.string().required(() => i18n.t("validation.required")),
    //   message: Yup.string().required("Obrigatório"),
});

const QuickMessageDialog = ({ open, onClose, quickemessageId, reload }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const { profile } = user;
    const messageInputRef = useRef();

    const initialState = {
        shortcode: "",
        message: "",
        geral: false,
        status: true,
        documentTemplateId: "",
    };

    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [quickemessage, setQuickemessage] = useState(initialState);
    // Arquivos escolhidos e ainda não enviados ao servidor.
    const [attachments, setAttachments] = useState([]);
    // Anexo já salvo que o usuário pediu para excluir (confirmação pendente).
    const [mediaToDelete, setMediaToDelete] = useState(null);
    const [documentTemplates, setDocumentTemplates] = useState([]);
    const attachmentFile = useRef(null);

    const savedMedias = getQuickMessageMedias(quickemessage);
    const totalAttachments = savedMedias.length + attachments.length;
    const maxReached = totalAttachments >= MAX_QUICKMESSAGE_MEDIAS;

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { data } = await api.get("/document-templates/list");
                setDocumentTemplates(data);
            } catch (err) {
                // silencioso: usuário pode não ter modelos cadastrados
            }
        })();
    }, [open]);

    useEffect(() => {
        try {
            (async () => {
                if (!quickemessageId) return;

                const { data } = await api.get(`/quick-messages/${quickemessageId}`);

                setQuickemessage((prevState) => {
                    return {
                        ...prevState,
                        ...data,
                        documentTemplateId: data.documentTemplateId || "",
                    };
                });
            })();
        } catch (err) {
            toastError(err);
        }
    }, [quickemessageId, open]);

    const handleClose = () => {
        setQuickemessage(initialState);
        setAttachments([]);
        setMediaToDelete(null);
        onClose();
    };

    const handleAttachmentFile = (e) => {
        const selecionados = Array.from(e.target.files || []);
        // Zera o input: sem isso, escolher o mesmo arquivo de novo não dispara onChange.
        e.target.value = null;

        if (selecionados.length === 0) return;

        const disponivel = MAX_QUICKMESSAGE_MEDIAS - totalAttachments;
        if (disponivel <= 0) {
            toast.warn(
                i18n.t("quickMessages.attachments.maxReached", {
                    max: MAX_QUICKMESSAGE_MEDIAS,
                })
            );
            return;
        }

        if (selecionados.length > disponivel) {
            toast.warn(
                i18n.t("quickMessages.attachments.maxReached", {
                    max: MAX_QUICKMESSAGE_MEDIAS,
                })
            );
        }

        setAttachments((prev) => [...prev, ...selecionados.slice(0, disponivel)]);
    };

    const handleRemovePendingAttachment = (index) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    // Envia todos os anexos pendentes em um único FormData.
    // typeArch precisa ir ANTES dos arquivos: o multer lê req.body.typeArch no
    // destination para escolher a pasta public/quickMessage.
    const uploadAttachments = async (quickMessageId) => {
        if (attachments.length === 0) return;

        const formData = new FormData();
        formData.append("typeArch", "quickMessage");
        attachments.forEach((file) => {
            formData.append("file", file);
            formData.append("mediaType", file.type || "");
        });

        const { data } = await api.post(
            `/quick-messages/${quickMessageId}/media-upload`,
            formData
        );

        if (data?.rejeitados?.length) {
            toast.warn(
                i18n.t("quickMessages.attachments.rejected", {
                    files: data.rejeitados.join(", "),
                })
            );
        }
    };

    const handleSaveQuickeMessage = async (values) => {

        const quickemessageData = {
            ...values,
            isMedia: true,
            documentTemplateId: values.documentTemplateId || null,
        };

        try {
            if (quickemessageId) {
                await api.put(`/quick-messages/${quickemessageId}`, quickemessageData);
                await uploadAttachments(quickemessageId);
            } else {
                const { data } = await api.post("/quick-messages", quickemessageData);
                await uploadAttachments(data.id);
            }
            toast.success(i18n.t("quickMessages.toasts.success"));
            if (typeof reload == "function") {

                reload();
            }
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    const handleAskDeleteMedia = (media) => {
        setMediaToDelete(media);
        setConfirmationOpen(true);
    };

    const handleConfirmDeleteMedia = async () => {
        if (!mediaToDelete) return;

        try {
            if (mediaToDelete.id) {
                await api.delete(
                    `/quick-messages/${quickemessage.id}/media-upload/${mediaToDelete.id}`
                );
                setQuickemessage((prev) => ({
                    ...prev,
                    medias: (prev.medias || []).filter((m) => m.id !== mediaToDelete.id),
                }));
            } else {
                // Anexo legado (colunas mediaPath/mediaName): a rota sem :mediaId
                // remove todos os anexos da resposta rápida.
                await api.delete(`/quick-messages/${quickemessage.id}/media-upload`);
                setQuickemessage((prev) => ({
                    ...prev,
                    medias: [],
                    mediaPath: null,
                    mediaName: null,
                }));
            }

            toast.success(i18n.t("quickMessages.toasts.deleted"));
            if (typeof reload == "function") {

                reload();
            }
        } catch (err) {
            toastError(err);
        } finally {
            setMediaToDelete(null);
        }
    };

    const handleClickMsgVar = async (msgVar, setValueFunc) => {
        const el = messageInputRef.current;
        const firstHalfText = el.value.substring(0, el.selectionStart);
        const secondHalfText = el.value.substring(el.selectionEnd);
        const newCursorPos = el.selectionStart + msgVar.length;

        setValueFunc("message", `${firstHalfText}${msgVar}${secondHalfText}`);

        await new Promise(r => setTimeout(r, 100));
        messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
    };

    return (
        <div className={classes.root}>
            <ConfirmationModal
                title={i18n.t("quickMessages.confirmationModal.deleteTitle")}
                open={confirmationOpen}
                onClose={() => setConfirmationOpen(false)}
                onConfirm={handleConfirmDeleteMedia}
            >
                {mediaToDelete && !mediaToDelete.id
                    ? i18n.t("quickMessages.attachments.deleteAllMessage")
                    : i18n.t("quickMessages.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="xs"
                fullWidth
                scroll="paper"
            >
                <DialogTitle id="form-dialog-title">
                    {quickemessageId
                        ? `${i18n.t("quickMessages.dialog.edit")}`
                        : `${i18n.t("quickMessages.dialog.add")}`}
                </DialogTitle>
                <div style={{ display: "none" }}>
                    <input
                        type="file"
                        multiple
                        ref={attachmentFile}
                        onChange={(e) => handleAttachmentFile(e)}
                    />
                </div>
                <Formik
                    initialValues={quickemessage}
                    enableReinitialize={true}
                    validationSchema={QuickeMessageSchema}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSaveQuickeMessage(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, setFieldValue, values }) => (
                        <Form>
                            <DialogContent dividers>
                                <Grid spacing={2} container>
                                    <Grid xs={12} item>
                                        <Field
                                            as={TextField}
                                            autoFocus
                                            label={i18n.t("quickMessages.dialog.shortcode")}
                                            name="shortcode"
                                            error={touched.shortcode && Boolean(errors.shortcode)}
                                            helperText={touched.shortcode && errors.shortcode}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid xs={12} item>
                                        <Field
                                            as={TextField}
                                            label={i18n.t("quickMessages.dialog.message")}
                                            name="message"
                                            inputRef={messageInputRef}
                                            error={touched.message && Boolean(errors.message)}
                                            helperText={touched.message && errors.message}
                                            variant="outlined"
                                            margin="dense"
                                            multiline={true}
                                            rows={7}
                                            fullWidth
                                        // disabled={quickemessage.mediaPath || attachment ? true : false}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <MessageVariablesPicker
                                            disabled={isSubmitting}
                                            onClick={value => handleClickMsgVar(value, setFieldValue)}
                                        />
                                    </Grid>
                                    <Grid xs={12} item>
                                        <FormControl
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        >
                                            <InputLabel id="documentTemplate-label">
                                                {i18n.t("documentTemplates.sendModal.selectTemplate")}
                                            </InputLabel>
                                            <Field
                                                as={Select}
                                                labelId="documentTemplate-label"
                                                label={i18n.t("documentTemplates.sendModal.selectTemplate")}
                                                name="documentTemplateId"
                                            >
                                                <MenuItem value="">{i18n.t("common.none")}</MenuItem>
                                                {documentTemplates.map((template) => (
                                                    <MenuItem key={template.id} value={template.id}>
                                                        {template.name}
                                                    </MenuItem>
                                                ))}
                                            </Field>
                                        </FormControl>
                                    </Grid>
                                    {totalAttachments > 0 && (
                                        <Grid xs={12} item>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                {`${i18n.t("quickMessages.attachments.title")} (${totalAttachments}/${MAX_QUICKMESSAGE_MEDIAS})`}
                                            </Typography>

                                            {/* Anexos já salvos: excluir chama a API */}
                                            {savedMedias.map((media, index) => (
                                                <div
                                                    key={media.id ?? `legacy-${index}`}
                                                    className={classes.attachmentRow}
                                                >
                                                    <Button
                                                        startIcon={<AttachFileIcon />}
                                                        className={classes.attachmentName}
                                                        href={media.mediaPath || undefined}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {media.mediaName || media.mediaPath}
                                                    </Button>
                                                    <IconButton
                                                        onClick={() => handleAskDeleteMedia(media)}
                                                        color="secondary"
                                                    >
                                                        <DeleteOutlineIcon color="secondary" />
                                                    </IconButton>
                                                </div>
                                            ))}

                                            {/* Anexos ainda não enviados: excluir é só local */}
                                            {attachments.map((file, index) => (
                                                <div
                                                    key={`pending-${index}-${file.name}`}
                                                    className={classes.attachmentRow}
                                                >
                                                    <Button
                                                        startIcon={<AttachFileIcon />}
                                                        className={classes.attachmentName}
                                                    >
                                                        {file.name}
                                                    </Button>
                                                    <IconButton
                                                        onClick={() => handleRemovePendingAttachment(index)}
                                                        color="secondary"
                                                    >
                                                        <DeleteOutlineIcon color="secondary" />
                                                    </IconButton>
                                                </div>
                                            ))}
                                        </Grid>
                                    )}
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                {/* Sempre visível: só é desabilitado ao atingir o limite de anexos */}
                                <Tooltip
                                    title={
                                        maxReached
                                            ? i18n.t("quickMessages.attachments.maxReached", {
                                                max: MAX_QUICKMESSAGE_MEDIAS,
                                            })
                                            : ""
                                    }
                                >
                                    <span>
                                        <Button
                                            color="primary"
                                            onClick={() => attachmentFile.current.click()}
                                            disabled={isSubmitting || maxReached}
                                            variant="outlined"
                                        >
                                            {i18n.t("quickMessages.buttons.attach")}
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("quickMessages.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {quickemessageId
                                        ? `${i18n.t("quickMessages.buttons.edit")}`
                                        : `${i18n.t("quickMessages.buttons.add")}`}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default QuickMessageDialog;